import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Link, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ArrowBack } from '@mui/icons-material';

/**
 * Política de Privacidad de SuitOrbit (producto: KobraIA).
 *
 * NOTA: Este es un borrador de redacción genérica (derechos GDPR / ARCO) pensado
 * como punto de partida. Debe ser revisado y adaptado por asesoría legal antes de
 * publicarse en producción.
 *
 * Colores de marca alineados con Landing / Login.
 */
const K = {
  green: '#6BD56F',
  blue: '#4185F1',
  navy: '#121826',
};

const C = {
  ...K,
  textMuted: alpha(K.navy, 0.62),
};

const LOGO_SRC = `${process.env.PUBLIC_URL || ''}/images/logotipo_kobraIA_removebg.png`;

/** Empresa y producto. */
const COMPANY = 'SuitOrbit';
const PRODUCT = 'KobraIA';
const CONTACT_EMAIL = 'soporte@suitorbit.com';
/** Fecha de última actualización (formato humano, es-ES). */
const LAST_UPDATED = '5 de julio de 2026';

/** Secciones del documento: título + párrafos y/o lista de viñetas. */
const SECTIONS = [
  {
    title: '1. Introducción',
    paragraphs: [
      `${COMPANY} ("nosotros", "nuestro" o la "Empresa") es responsable del tratamiento de los datos personales que recopilamos a través de ${PRODUCT} (el "Servicio"). Esta Política de Privacidad describe qué datos recopilamos, con qué finalidad, cómo los protegemos y qué derechos tienes sobre ellos.`,
      `Al utilizar el Servicio aceptas las prácticas descritas en esta política. Si no estás de acuerdo con ella, te pedimos que no utilices el Servicio.`,
    ],
  },
  {
    title: '2. Datos que recopilamos',
    paragraphs: ['Podemos recopilar y tratar las siguientes categorías de datos:'],
    bullets: [
      'Datos de cuenta: nombre, correo electrónico, empresa y credenciales de acceso.',
      'Datos de uso: interacciones con el Servicio, registros de actividad, dirección IP, tipo de dispositivo y navegador.',
      'Datos de contenido: información, mensajes y documentos que cargas o generas dentro del Servicio.',
      'Datos de terceros conectados: información proveniente de integraciones o conectores que autorizas expresamente.',
    ],
  },
  {
    title: '3. Finalidades del tratamiento',
    paragraphs: ['Utilizamos tus datos personales para:'],
    bullets: [
      'Proporcionar, operar y mantener el Servicio.',
      'Autenticar usuarios y gestionar el acceso a la cuenta.',
      'Mejorar, personalizar y desarrollar nuevas funcionalidades.',
      'Comunicarnos contigo sobre soporte, actualizaciones y avisos de seguridad.',
      'Cumplir con obligaciones legales y prevenir fraude o usos indebidos.',
    ],
  },
  {
    title: '4. Base legal del tratamiento',
    paragraphs: [
      'Tratamos tus datos personales sobre las siguientes bases legales, según corresponda: (i) la ejecución del contrato para prestarte el Servicio; (ii) tu consentimiento, cuando aplique; (iii) nuestro interés legítimo en operar y mejorar el Servicio; y (iv) el cumplimiento de obligaciones legales.',
    ],
  },
  {
    title: '5. Compartir datos con terceros',
    paragraphs: [
      'No vendemos tus datos personales. Podemos compartirlos únicamente con: proveedores de infraestructura y servicios que actúan como encargados del tratamiento bajo acuerdos de confidencialidad; autoridades competentes cuando la ley lo exija; y terceros que autorices expresamente mediante integraciones.',
    ],
  },
  {
    title: '6. Transferencias internacionales',
    paragraphs: [
      'Tus datos pueden ser tratados o almacenados en servidores ubicados en distintos países. Cuando realizamos transferencias internacionales, aplicamos las garantías adecuadas exigidas por la normativa aplicable para proteger tus datos.',
    ],
  },
  {
    title: '7. Conservación de los datos',
    paragraphs: [
      'Conservamos tus datos personales únicamente durante el tiempo necesario para cumplir las finalidades descritas en esta política y las obligaciones legales aplicables. Cuando dejan de ser necesarios, los eliminamos o anonimizamos de forma segura.',
    ],
  },
  {
    title: '8. Seguridad',
    paragraphs: [
      'Implementamos medidas técnicas y organizativas razonables para proteger tus datos frente a accesos no autorizados, pérdida o alteración. No obstante, ningún sistema es completamente seguro, por lo que no podemos garantizar una seguridad absoluta.',
    ],
  },
  {
    title: '9. Tus derechos',
    paragraphs: [
      'De acuerdo con la normativa aplicable, puedes ejercer los derechos de Acceso, Rectificación, Cancelación y Oposición (derechos ARCO), así como los derechos de supresión, limitación del tratamiento, portabilidad y a retirar tu consentimiento (bajo el RGPD y normativas equivalentes).',
    ],
    bullets: [
      'Acceder a los datos personales que tenemos sobre ti.',
      'Solicitar la rectificación de datos inexactos o incompletos.',
      'Solicitar la supresión o cancelación de tus datos.',
      'Oponerte o solicitar la limitación de determinados tratamientos.',
      'Solicitar la portabilidad de tus datos y retirar tu consentimiento en cualquier momento.',
    ],
    after: [
      `Para ejercer cualquiera de estos derechos, escríbenos a ${CONTACT_EMAIL}. Responderemos dentro de los plazos establecidos por la normativa aplicable.`,
    ],
  },
  {
    title: '10. Cookies y tecnologías similares',
    paragraphs: [
      'Utilizamos cookies y tecnologías similares para mantener tu sesión, recordar tus preferencias y analizar el uso del Servicio. Puedes gestionar las cookies desde la configuración de tu navegador; deshabilitarlas puede afectar el funcionamiento del Servicio.',
    ],
  },
  {
    title: '11. Menores de edad',
    paragraphs: [
      'El Servicio no está dirigido a menores de edad. No recopilamos de forma consciente datos personales de menores. Si detectamos que hemos recopilado datos de un menor sin la autorización correspondiente, los eliminaremos.',
    ],
  },
  {
    title: '12. Cambios a esta política',
    paragraphs: [
      'Podemos actualizar esta Política de Privacidad periódicamente. Publicaremos la versión vigente en esta página e indicaremos la fecha de última actualización. Te recomendamos revisarla de forma regular.',
    ],
  },
  {
    title: '13. Contacto',
    paragraphs: [
      `Si tienes preguntas sobre esta Política de Privacidad o sobre el tratamiento de tus datos, contáctanos en ${CONTACT_EMAIL}.`,
    ],
  },
];

export function PrivacyPolicy() {
  const [logoFailed, setLogoFailed] = React.useState(false);

  // El enlace de origen suele estar al fondo del footer; al navegar aquí
  // conservamos la posición de scroll, así que forzamos volver al inicio.
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box
        component="header"
        sx={{
          borderBottom: `1px solid ${alpha(K.navy, 0.08)}`,
          py: 2,
          bgcolor: '#fff',
        }}
      >
        <Container maxWidth="md">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              component={RouterLink}
              to="/"
              sx={{ textDecoration: 'none' }}
            >
              {!logoFailed ? (
                <Box
                  component="img"
                  src={LOGO_SRC}
                  alt={PRODUCT}
                  sx={{ height: 28, opacity: 0.9 }}
                  onError={() => setLogoFailed(true)}
                />
              ) : (
                <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
                  <Box component="span" sx={{ color: C.blue }}>
                    Kobra
                  </Box>
                  <Box component="span" sx={{ color: alpha(K.navy, 0.75) }}>
                    {' '}
                    IA
                  </Box>
                </Typography>
              )}
            </Stack>
            <Link
              component={RouterLink}
              to="/"
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: C.textMuted,
                fontSize: '0.9rem',
                '&:hover': { color: C.blue },
              }}
            >
              <ArrowBack sx={{ fontSize: 18 }} />
              Volver al inicio
            </Link>
          </Stack>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="md" sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
        <Typography
          variant="h3"
          sx={{ fontWeight: 800, color: K.navy, fontSize: { xs: '1.8rem', md: '2.4rem' } }}
        >
          Política de Privacidad
        </Typography>
        <Typography sx={{ color: C.textMuted, mt: 1, mb: 5 }}>
          Última actualización: {LAST_UPDATED}
        </Typography>

        <Stack spacing={4}>
          {SECTIONS.map((section) => (
            <Box component="section" key={section.title}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: K.navy, fontSize: { xs: '1.15rem', md: '1.35rem' }, mb: 1.5 }}
              >
                {section.title}
              </Typography>
              {section.paragraphs?.map((text, i) => (
                <Typography
                  key={i}
                  sx={{ color: alpha(K.navy, 0.8), lineHeight: 1.7, mb: 1.5 }}
                >
                  {text}
                </Typography>
              ))}
              {section.bullets && (
                <Box component="ul" sx={{ pl: 3, my: 1, color: alpha(K.navy, 0.8) }}>
                  {section.bullets.map((item, i) => (
                    <Typography component="li" key={i} sx={{ lineHeight: 1.7, mb: 0.75 }}>
                      {item}
                    </Typography>
                  ))}
                </Box>
              )}
              {section.after?.map((text, i) => (
                <Typography
                  key={i}
                  sx={{ color: alpha(K.navy, 0.8), lineHeight: 1.7, mt: 1.5 }}
                >
                  {text}
                </Typography>
              ))}
            </Box>
          ))}
        </Stack>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{ borderTop: `1px solid ${alpha(K.navy, 0.08)}`, py: 3, bgcolor: '#fff' }}
      >
        <Container maxWidth="md">
          <Typography sx={{ color: C.textMuted, fontSize: '0.85rem', textAlign: 'center' }}>
            © {new Date().getFullYear()} {COMPANY}. {PRODUCT}. Todos los derechos reservados.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}
